// src/pages/HolderScore.tsx
import { useAccount, useReadContract } from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useState, useEffect, useRef } from 'react'
import { Search, Wallet, Trophy, ExternalLink, Copy, CheckCheck, ChevronRight } from 'lucide-react'
import clsx from 'clsx'

const CONTRACT = '0x818030837e8350ba63e64d7dc01a547fa73c8279' as const

const ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

const TIERS = [
  {
    min: 10,
    name: 'SQUAD LEGEND',
    emoji: '👑',
    image: '/images/legend.png',
    gradient: 'from-yellow-400 via-amber-300 to-yellow-600',
    gradientBg: 'from-yellow-500/10 via-amber-500/5 to-transparent',
    border: 'border-yellow-500/50',
    glow: '0 0 40px rgba(234,179,8,0.25), 0 0 80px rgba(234,179,8,0.1)',
    text: 'text-yellow-400',
    badge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
    message: 'You ARE the 10K Squad. Absolute royalty.',
    sub: 'The rarest of the rare. The entire collection bows.',
  },
  {
    min: 5,
    name: 'INSANE HODLER',
    emoji: '🔥',
    image: '/images/insane.png',
    gradient: 'from-orange-400 via-red-400 to-orange-600',
    gradientBg: 'from-orange-500/10 via-red-500/5 to-transparent',
    border: 'border-orange-500/50',
    glow: '0 0 40px rgba(249,115,22,0.25), 0 0 80px rgba(249,115,22,0.1)',
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
    message: 'Absolutely unhinged.',
    sub: 'We mean that as the highest compliment possible.',
  },
  {
    min: 3,
    name: 'DIAMOND HANDS',
    emoji: '💎',
    image: '/images/diamond.png',
    gradient: 'from-cyan-400 via-blue-400 to-cyan-600',
    gradientBg: 'from-cyan-500/10 via-blue-500/5 to-transparent',
    border: 'border-cyan-400/50',
    glow: '0 0 40px rgba(34,211,238,0.25), 0 0 80px rgba(34,211,238,0.1)',
    text: 'text-cyan-400',
    badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
    message: 'Built different.',
    sub: 'Never selling, never wavering. Pure conviction.',
  },
  {
    min: 2,
    name: 'PARROT GANG',
    emoji: '🦜',
    image: '/images/parrot.png',
    gradient: 'from-green-400 via-emerald-400 to-green-600',
    gradientBg: 'from-green-500/10 via-emerald-500/5 to-transparent',
    border: 'border-green-500/50',
    glow: '0 0 40px rgba(34,197,94,0.25), 0 0 80px rgba(34,197,94,0.1)',
    text: 'text-green-400',
    badge: 'bg-green-500/20 text-green-300 border-green-500/40',
    message: 'Loyal to the flock.',
    sub: 'The gang grows stronger with every addition.',
  },
  {
    min: 1,
    name: 'SQUAD MEMBER',
    emoji: '🎯',
    image: '/images/member.png',
    gradient: 'from-pink-400 via-fuchsia-400 to-pink-600',
    gradientBg: 'from-pink-500/10 via-fuchsia-500/5 to-transparent',
    border: 'border-pink-500/50',
    glow: '0 0 40px rgba(236,72,153,0.25), 0 0 80px rgba(236,72,153,0.1)',
    text: 'text-pink-400',
    badge: 'bg-pink-500/20 text-pink-300 border-pink-500/40',
    message: 'Welcome to the squad, fren.',
    sub: 'This is just the beginning of your journey.',
  },
  {
    min: 0,
    name: 'NGMI',
    emoji: '😢',
    image: '/images/ngmi.png',
    gradient: 'from-gray-500 via-slate-400 to-gray-600',
    gradientBg: 'from-gray-500/5 via-slate-500/5 to-transparent',
    border: 'border-gray-600/40',
    glow: '0 0 20px rgba(107,114,128,0.15)',
    text: 'text-gray-400',
    badge: 'bg-gray-500/20 text-gray-400 border-gray-600/40',
    message: 'You hold zero.',
    sub: 'The floor is right there ser... just saying.',
  },
]

function getTier(balance: number) {
  return TIERS.find((t) => balance >= t.min) ?? TIERS[TIERS.length - 1]
}

function shortAddr(addr: string) {
  if (addr.length < 10) return addr
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`
}

function isValidAddress(addr: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(addr.trim())
}

function CountUp({ to }: { to: number }) {
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (to === 0) { setVal(0); return }
    let start = 0
    const steps = 30
    const inc = to / steps
    const id = setInterval(() => {
      start += inc
      if (start >= to) { setVal(to); clearInterval(id) }
      else setVal(Math.floor(start))
    }, 30)
    return () => clearInterval(id)
  }, [to])
  return <>{val}</>
}

function TierImage({ tier }: { tier: typeof TIERS[0] }) {
  const [failed, setFailed] = useState(false)
  return failed ? (
    <div className="w-full h-full flex items-center justify-center" style={{ fontSize: '5rem' }}>
      {tier.emoji}
    </div>
  ) : (
    <img
      src={tier.image}
      alt={tier.name}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  )
}

export default function HolderScore() {
  const { address: connectedAddress, isConnected } = useAccount()

  const [inputAddr, setInputAddr]     = useState('')
  const [checkedAddr, setCheckedAddr] = useState<string | null>(null)
  const [inputError, setInputError]   = useState('')
  const [copied, setCopied]           = useState(false)
  const [revealed, setRevealed]       = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const queryAddr = (checkedAddr ?? (isConnected ? connectedAddress : null)) as `0x${string}` | undefined

  const { data: rawBalance, isLoading, isFetching } = useReadContract({
    address: CONTRACT,
    abi: ABI,
    functionName: 'balanceOf',
    args: queryAddr ? [queryAddr] : undefined,
    query: { enabled: !!queryAddr },
  })

  const balance = rawBalance !== undefined ? Number(rawBalance) : null
  const tier    = balance !== null ? getTier(balance) : null

  useEffect(() => {
    if (balance !== null && !isLoading) {
      setRevealed(false)
      const t = setTimeout(() => setRevealed(true), 100)
      return () => clearTimeout(t)
    }
  }, [balance, isLoading, queryAddr])

  useEffect(() => {
    if (isConnected && connectedAddress) {
      setCheckedAddr(null)
      setInputAddr('')
    }
  }, [isConnected, connectedAddress])

  function handleCheck() {
    const val = inputAddr.trim()
    if (!val) { setInputError('Enter a wallet address'); return }
    if (!isValidAddress(val)) { setInputError('Invalid address — must be 0x + 40 hex chars'); return }
    setInputError('')
    setCheckedAddr(val)
  }

  function handleCopyShare() {
    if (!tier || balance === null) return
    const addr = queryAddr ? shortAddr(queryAddr) : ''
    const text = `${addr} is a ${tier.emoji} ${tier.name} on @10ksquadbot!\nHolding ${balance} 10K Squad NFT${balance !== 1 ? 's' : ''} on Monad 🔥\nCheck your score → https://10ksquadbot.vercel.app/score`
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  const showResult  = !!queryAddr && !isLoading && balance !== null
  const showLoading = !!queryAddr && (isLoading || isFetching) && balance === null

  return (
    <div className="relative min-h-screen bg-[#0a0008] overflow-x-hidden">

      {/* Background blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-pink-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-80 h-80 bg-purple-600/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-72 h-72 bg-pink-500/6 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 py-12 sm:py-16 flex flex-col gap-8">

        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-pink-600/10 border border-pink-600/20 text-pink-300/70 text-xs font-mono px-3 py-1.5 rounded-full mb-4">
            <Trophy size={10} />
            SQUAD SCORER
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-white tracking-widest leading-none mb-3">
            CHECK YOUR
            <br />
            <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-pink-500 bg-clip-text text-transparent">
              TIER
            </span>
          </h1>
          <p className="text-pink-200/40 font-mono text-xs sm:text-sm">
            Enter any wallet · Connect yours · See your rank
          </p>
        </div>

        {/* Input section */}
        <div className="flex flex-col gap-3">

          {/* Address input */}
          <div className="flex flex-col gap-2">
            <div className={clsx(
              'flex items-center gap-2 rounded-xl border bg-pink-950/20 backdrop-blur-sm px-3 sm:px-4 py-3 transition-all duration-200',
              inputError
                ? 'border-red-500/50'
                : 'border-pink-800/40 focus-within:border-pink-500/60 focus-within:bg-pink-950/30'
            )}>
              <Search size={15} className="text-pink-400/50 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={inputAddr}
                onChange={(e) => { setInputAddr(e.target.value); setInputError('') }}
                onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
                placeholder="0x... paste any wallet address"
                className="flex-1 bg-transparent text-white font-mono text-xs sm:text-sm placeholder:text-pink-200/20 outline-none min-w-0"
                spellCheck={false}
                autoComplete="off"
              />
              {inputAddr && (
                <button
                  onClick={handleCheck}
                  className="shrink-0 bg-pink-600 hover:bg-pink-500 active:scale-95 text-white text-xs font-mono px-3 py-1.5 rounded-lg transition-all"
                >
                  CHECK
                </button>
              )}
            </div>
            {inputError && (
              <p className="text-red-400/80 text-xs font-mono pl-1">{inputError}</p>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-pink-900/30" />
            <span className="text-pink-200/20 text-xs font-mono">or connect wallet</span>
            <div className="flex-1 h-px bg-pink-900/30" />
          </div>

          {/* RainbowKit connect */}
          <div className="flex flex-col items-center gap-2">
            <ConnectButton />
            <p className="text-pink-200/20 text-xs font-mono">No signing · Read-only</p>
          </div>
        </div>

        {/* Loading */}
        {showLoading && (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="relative w-14 h-14">
              <div className="absolute inset-0 rounded-full border-2 border-pink-900/30" />
              <div className="absolute inset-0 rounded-full border-2 border-t-pink-500 animate-spin" />
              <div className="absolute inset-2 rounded-full border border-t-pink-400/50 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
            </div>
            <p className="text-pink-300/40 font-mono text-xs animate-pulse">Scanning Monad chain…</p>
          </div>
        )}

        {/* Result card */}
        {showResult && tier && balance !== null && (
          <div
            className={clsx(
              'rounded-2xl border overflow-hidden transition-all duration-500',
              revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
              tier.border,
            )}
            style={{ boxShadow: tier.glow }}
          >
            <div className={clsx('h-1 w-full bg-gradient-to-r', tier.gradient)} />

            <div className="bg-[#0a0008] p-5 sm:p-6 flex flex-col gap-5">

              {/* Address + badge */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Wallet size={12} className="text-pink-400/40 shrink-0" />
                  <span className="font-mono text-xs text-pink-200/30 truncate">
                    {queryAddr && shortAddr(queryAddr)}
                  </span>
                </div>
                <span className={clsx('text-xs font-mono px-2 py-0.5 rounded-md border shrink-0', tier.badge)}>
                  {tier.emoji} {balance} held
                </span>
              </div>

              {/* Image + tier info */}
              <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                <div
                  className={clsx('w-36 h-36 sm:w-44 sm:h-44 rounded-2xl overflow-hidden border-2 shrink-0', tier.border)}
                  style={{ boxShadow: tier.glow }}
                >
                  <TierImage tier={tier} />
                </div>

                <div className="flex flex-col gap-3 text-center sm:text-left min-w-0 w-full">
                  <div>
                    <p className={clsx('font-display text-2xl sm:text-3xl tracking-widest leading-none mb-1', tier.text)}>
                      {tier.name}
                    </p>
                    <p className="text-white/80 font-body text-base sm:text-lg font-semibold">
                      {tier.message}
                    </p>
                    <p className="text-pink-200/40 font-body text-sm mt-1">
                      {tier.sub}
                    </p>
                  </div>
                  <div className="flex items-baseline gap-1 justify-center sm:justify-start">
                    <span className={clsx('font-display text-5xl sm:text-6xl leading-none', tier.text)}>
                      <CountUp to={balance} />
                    </span>
                    <span className="text-pink-200/30 font-mono text-xs">
                      NFT{balance !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <NextTierBar balance={balance} tier={tier} />

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleCopyShare}
                  className="flex-1 flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-500 active:scale-95 text-white text-xs font-mono py-2.5 px-4 rounded-xl transition-all"
                >
                  {copied
                    ? <><CheckCheck size={13} /> Copied!</>
                    : <><Copy size={13} /> Copy &amp; Share</>
                  }
                </button>
                <a
                  href="https://opensea.io/collection/the-10k-squad-350905768"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 border border-pink-800/50 hover:border-pink-600/60 hover:bg-pink-950/40 text-pink-300 text-xs font-mono py-2.5 px-4 rounded-xl transition-all"
                >
                  <ExternalLink size={13} />
                  {balance === 0 ? 'Buy Now' : 'Get More'}
                </a>
              </div>

            </div>
          </div>
        )}

        {/* All tiers */}
        <div>
          <p className="text-pink-200/20 font-mono text-xs tracking-widest text-center mb-4">ALL TIERS</p>
          <div className="flex flex-col gap-2">
            {TIERS.map((t, i) => {
              const isYours = balance !== null && (
                (t.min === 0 && balance === 0) ||
                (t.min > 0 && balance >= t.min && (i === 0 || balance < TIERS[i - 1].min))
              )
              return (
                <div
                  key={t.name}
                  className={clsx(
                    'flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300',
                    isYours
                      ? clsx(t.border, 'bg-gradient-to-r', t.gradientBg)
                      : 'border-pink-900/20 bg-pink-950/10',
                  )}
                  style={isYours ? { boxShadow: t.glow } : undefined}
                >
                  <span className="text-lg shrink-0">{t.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className={clsx('font-display text-sm tracking-wide', isYours ? t.text : 'text-pink-200/30')}>
                      {t.name}
                    </p>
                    <p className="text-pink-200/20 text-xs font-mono">
                      {t.min === 0 ? '0 NFTs' : t.min === 10 ? '10+ NFTs' : `${t.min}–${TIERS[i - 1].min - 1} NFTs`}
                    </p>
                  </div>
                  {isYours && (
                    <span className={clsx('text-xs font-mono shrink-0 flex items-center gap-1', t.text)}>
                      <ChevronRight size={10} /> YOU
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

      </div>
    </div>
  )
}

function NextTierBar({ balance, tier }: { balance: number; tier: typeof TIERS[0] }) {
  const tierIndex = TIERS.findIndex((t) => balance >= t.min)
  const nextTier  = TIERS[tierIndex - 1]

  if (!nextTier || balance >= 10) {
    return (
      <p className="text-yellow-400/50 font-mono text-xs text-center py-1">
        👑 MAX TIER — You're at the top ser
      </p>
    )
  }

  const needed   = nextTier.min - balance
  const progress = Math.min((balance / nextTier.min) * 100, 100)

  return (
    <div className="w-full flex flex-col gap-1.5">
      <div className="flex justify-between text-xs font-mono text-pink-200/30 flex-wrap gap-1">
        <span>{tier.name}</span>
        <span>{needed} more → {nextTier.emoji} {nextTier.name}</span>
      </div>
      <div className="w-full h-1.5 bg-pink-950/60 rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full bg-gradient-to-r transition-all duration-700', tier.gradient)}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}